import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "rgb(var(--tc-3fd07a))";

function fmtDM(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
}

export default function Antidoping() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const teamId = user?.team_id;
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // Admin: di default vede SOLO i propri controlli (come un presidente).
    // Con il toggle può rivedere tutti i controlli del torneo.
    const [showAll, setShowAll] = useState(false);
    const viewingAll = isAdmin && showAll;

    useEffect(() => {
        let active = true;
        const url = viewingAll
            ? "/admin/antidoping"
            : (teamId ? `/teams/${teamId}/antidoping` : (isAdmin ? "/admin/antidoping" : null));
        if (!url) { setLoading(false); return undefined; }
        setLoading(true);
        api.get(url)
            .then(({ data: d }) => { if (active) setData(d); })
            .catch((e) => { if (active) setError(e.response?.data?.detail || "Errore"); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [viewingAll, teamId, isAdmin]);

    const list = data?.assignments || [];

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="antidoping-page">
            <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🧪</span>
                <h1 className="op-title text-2xl" style={{ color: ACCENT }}>Antidoping</h1>
            </div>
            <p className="op-mono text-[12px] text-t8f95a3 mb-4 max-w-2xl leading-relaxed">
                {viewingAll
                    ? "Tutti i controlli antidoping attivi in questa decade (tutte le squadre). A inizio decade ~50% delle partite genera un controllo su un giocatore avversario."
                    : "Controlli che la tua squadra deve effettuare in questa decade. Sorteggio automatico a inizio decade: un giocatore avversario da controllare. Sparisce a fine decade."}
            </p>

            {isAdmin && teamId && (
                <div className="flex border border-t22252a rounded overflow-hidden self-start mb-6 w-fit" data-testid="antidoping-scope-toggle">
                    <button
                        onClick={() => setShowAll(false)}
                        className={`op-mono text-[11px] uppercase px-3 py-2 transition-colors ${!showAll ? "bg-t3fd07a text-t0a0c0f" : "text-t8f95a3 hover:bg-t15181d"}`}
                        data-testid="antidoping-scope-mine"
                    >
                        Solo i miei
                    </button>
                    <button
                        onClick={() => setShowAll(true)}
                        className={`op-mono text-[11px] uppercase px-3 py-2 transition-colors ${showAll ? "bg-t3fd07a text-t0a0c0f" : "text-t8f95a3 hover:bg-t15181d"}`}
                        data-testid="antidoping-scope-all"
                    >
                        Tutti
                    </button>
                </div>
            )}

            {loading && <div className="op-mono text-[12px] text-t5c6370">Caricamento…</div>}
            {error && <div className="op-mono text-[12px] text-tff4d4d">{error}</div>}
            {!loading && !error && !isAdmin && !teamId && (
                <div className="op-mono text-[12px] text-t8f95a3">Nessuna squadra associata al tuo account.</div>
            )}

            {!loading && !error && list.length === 0 && (isAdmin || teamId) && (
                <div className="op-card p-6 text-center" data-testid="antidoping-empty">
                    <div className="text-3xl mb-2">✅</div>
                    <div className="op-mono text-[13px] text-t8f95a3">Nessun controllo antidoping attivo in questa decade.</div>
                </div>
            )}

            <div className="space-y-3">
                {list.map((a) => (
                    <div key={a.id} className="op-card p-4 flex items-center justify-between gap-4 flex-wrap"
                        style={{ borderLeft: `3px solid ${ACCENT}` }} data-testid={`antidoping-item-${a.id}`}>
                        <div className="flex items-center gap-4">
                            <div className="op-mono text-[10px] text-t5c6370 uppercase w-28">
                                {a.competition}{a.giornata ? ` · G${a.giornata}` : ""}
                                <div className="text-t3a3d44">decade {fmtDM(a.decade_from)}–{fmtDM(a.decade_to)}</div>
                            </div>
                            <div>
                                <div className="op-mono text-[15px] text-tffffff font-semibold">
                                    {a.player?.name}
                                    <span className="ml-2 op-label text-[10px] px-1.5 py-0.5 border" style={{ borderColor: ACCENT, color: ACCENT }}>
                                        {a.player?.vg} VG
                                    </span>
                                    {a.player?.position && <span className="op-mono text-[10px] text-t5c6370 ml-2">{a.player.position}</span>}
                                </div>
                                <div className="op-mono text-[11px] text-t8f95a3 mt-0.5">
                                    da controllare · rosa di{" "}
                                    <Link to={`/squadre/${a.target_team_id}`} className="text-t2bb6ff hover:underline">
                                        {a.target_team_name}
                                    </Link>
                                </div>
                            </div>
                        </div>
                        {isAdmin && a.controller_team_name && (
                            <div className="op-mono text-[10px] text-t8f95a3 text-right">
                                controllore
                                <div className="text-tffffff">
                                    <Link to={`/squadre/${a.controller_team_id}`} className="hover:underline">{a.controller_team_name}</Link>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
